import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LANGUAGE } from '../../../../../shared/injection-tokens';
import { Language } from '../../../../../shared/salesforce-types';

@Component({
    selector: 'clamper',
    templateUrl: './clamp.component.html',
    styleUrls: ['./clamp.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ClampComponent implements AfterViewInit, OnChanges {
    @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;

    @Input() text = '';
    @Input() maxLines = 5;
    @Input() minimumHeight?: number;
    @Output() populateReadMoreVisibility = new EventEmitter();
    @Output() clampChanged = new EventEmitter<boolean>();

    /**
     * @description Use this binding to active line clamping logic when element is toggled
     * for example: <clamper [isActive]="selectedIndex === i"></clamper>
     */
    @Input()
    isActive = false;
    isClamped = true;
    collapsed = true;

    constructor(@Inject(DOCUMENT) private document: Document, @Inject(LANGUAGE) private language: BehaviorSubject<Language>) {}

    get readMoreLabel(): string {
        return this.language.value === Language.Es ? 'Leer más' : 'Read More';
    }

    get readLessLabel(): string {
        return this.language.value === Language.Es ? 'Leer menos' : 'Read Less';
    }

    get hasText(): boolean {
        return Boolean(this.text);
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.calculateLines(); // recalculate lines when window is resized
    }

    ngAfterViewInit(): void {
        this.calculateLines(); // recalculate lines when view is fully rendered
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isActive?.currentValue || changes.text || changes.maxLines || changes.minimumHeight) {
            this.calculateLines(); // recalculate lines when inputs that affect measurement change
        }
    }

    toggle() {
        this.collapsed = !this.collapsed;
        setTimeout(() => this.clampChanged.emit(this.collapsed), 10);
    }

    calculateLines() {
        setTimeout(() => {
            const window = this.document.defaultView;
            if (!window || !this.host) {
                return;
            }

            const hostElement = this.host.nativeElement;

            // Temporarily remove clamping to measure the element's full height.
            hostElement.style.setProperty('display', 'block', 'important');
            hostElement.style.setProperty('overflow', 'visible', 'important');
            hostElement.style.setProperty('-webkit-box-orient', 'initial', 'important');
            hostElement.style.setProperty('-webkit-line-clamp', 'initial');

            const style = window.getComputedStyle(hostElement);
            const parsedLineHeight = parseFloat(style.lineHeight);
            const fallbackLineHeight = parseFloat(style.fontSize) * 1.2;
            const lineHeight = Number.isNaN(parsedLineHeight) ? fallbackLineHeight : parsedLineHeight;
            const fullHeight = hostElement.scrollHeight;
            const numberOfLines = Math.round(fullHeight / lineHeight);

            if (numberOfLines > this.maxLines) {
                this.isClamped = true;
            } else {
                this.isClamped = false;
            }

            hostElement.style.removeProperty('display');
            hostElement.style.removeProperty('overflow');
            hostElement.style.removeProperty('-webkit-box-orient');
            hostElement.style.removeProperty('-webkit-line-clamp');
            hostElement.style.removeProperty('min-height');
        });
    }
}
